import { Component, OnInit, HostListener } from '@angular/core';
import { ContactusService } from '../service/contactus.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';

export interface Lead {
  firmname: string;
  email: string;
  person: string;
  phone: string;
  message?: string;   // added — optional since older records may not have it
  plan: string;
  country?: string;
  createdAt: string;
  _id: string;
}

@Component({
  selector: 'app-lexiz-lawyer',
  templateUrl: './lexiz-lawyer.component.html',
  styleUrls: ['./lexiz-lawyer.component.css']
})
export class LexizLawyerComponent implements OnInit {

  readonly PRODUCT_KEY   = 'LAUDITOR';
  readonly PRODUCT_LABEL = 'Lexiz Lawyer';

  // ─── Leads data ───────────────────────────────────────────────────────────
  allLeads: Lead[]      = [];
  filteredData: Lead[]  = [];
  dataSource: Lead[]    = [];
  isLoading: boolean    = false;

  // ─── Search ───────────────────────────────────────────────────────────────
  searchQuery: string = '';

  // ─── Sort ─────────────────────────────────────────────────────────────────
  sortColumn: string                  = '';
  sortDirection: 'asc' | 'desc' | '' = '';

  // ─── Pagination ───────────────────────────────────────────────────────────
  pageIndex: number = 0;
  pageSize: number  = 10;

  constructor(
    private contactusService: ContactusService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadLeads();
  }

  // ═══════════════════════════════════════════════════════════
  //  LEADS
  // ═══════════════════════════════════════════════════════════

  loadLeads(): void {
    this.isLoading = true;
    this.spinner.show();
    this.searchQuery   = '';
    this.sortColumn    = '';
    this.sortDirection = '';
    this.pageIndex     = 0;

    this.contactusService.getLeadsByProduct(this.PRODUCT_KEY).subscribe(
      (response: any) => {
        this.allLeads = Array.isArray(response) ? response : [];
        this.applyFilter();
        this.isLoading = false;
        this.spinner.hide();
      },
      (error: any) => {
        console.error('Error loading Lexiz Lawyer leads:', error);
        this.toastr.error('Failed to load leads', 'Error');
        this.allLeads   = [];
        this.dataSource = [];
        this.isLoading  = false;
        this.spinner.hide();
      }
    );
  }

  // ═══════════════════════════════════════════════════════════
  //  FILTER & SORT
  // ═══════════════════════════════════════════════════════════

  applyFilter(): void {
    const q = this.searchQuery.trim().toLowerCase();
    let filtered = [...this.allLeads];

    if (q) {
      filtered = filtered.filter(l =>
        (l.person   || '').toLowerCase().includes(q) ||
        (l.firmname || '').toLowerCase().includes(q) ||
        (l.email    || '').toLowerCase().includes(q) ||
        (l.phone    || '').toLowerCase().includes(q) ||
        (l.message  || '').toLowerCase().includes(q) ||
        (l.country  || '').toLowerCase().includes(q) ||
        (l.plan     || '').toLowerCase().includes(q)
      );
    }

    this.filteredData = filtered;
    this.pageIndex    = 0;

    if (this.sortColumn && this.sortDirection) {
      this.applySort();
    } else {
      this.dataSource = [...this.filteredData];
    }
  }

  applySort(): void {
    if (!this.sortColumn || this.sortDirection === '') {
      this.dataSource = [...this.filteredData];
      return;
    }

    const data = [...this.filteredData];
    const dir  = this.sortDirection === 'asc' ? 1 : -1;
    const col  = this.sortColumn as keyof Lead;

    data.sort((a, b) => {
      let valA = a[col];
      let valB = b[col];

      if (col === 'createdAt') {
        const tA = valA ? new Date(valA as string).getTime() : 0;
        const tB = valB ? new Date(valB as string).getTime() : 0;
        return (tA - tB) * dir;
      }

      valA = (valA || '').toString().toLowerCase();
      valB = (valB || '').toString().toLowerCase();
      if (valA < valB) return -1 * dir;
      if (valA > valB) return  1 * dir;
      return 0;
    });

    this.dataSource = data;
  }

  sortBy(column: string): void {
    if (this.sortColumn === column) {
      if      (this.sortDirection === 'asc')  { this.sortDirection = 'desc'; }
      else if (this.sortDirection === 'desc') { this.sortDirection = ''; this.sortColumn = ''; }
      else                                    { this.sortDirection = 'asc'; }
    } else {
      this.sortColumn    = column;
      this.sortDirection = 'asc';
    }
    this.applySort();
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column || this.sortDirection === '') {
      return 'bi-arrow-down-up sort-icon-neutral';
    }
    return this.sortDirection === 'asc'
      ? 'bi-arrow-up sort-icon-active'
      : 'bi-arrow-down sort-icon-active';
  }

  // ═══════════════════════════════════════════════════════════
  //  SEARCH
  // ═══════════════════════════════════════════════════════════

  onSearch(): void    { this.applyFilter(); }
  clearSearch(): void {
    this.searchQuery = '';
    this.applyFilter();
  }

  // ═══════════════════════════════════════════════════════════
  //  PAGINATION
  // ═══════════════════════════════════════════════════════════

  getPage(): Lead[] {
    const start = this.pageIndex * this.pageSize;
    return this.dataSource.slice(start, start + this.pageSize);
  }

  getTotalPages(): number {
    return Math.max(1, Math.ceil(this.dataSource.length / this.pageSize));
  }

  getPageNumbers(): number[] {
    const total   = this.getTotalPages();
    const current = this.pageIndex + 1;
    const maxVis  = 5;
    let start = Math.max(1, current - Math.floor(maxVis / 2));
    let end   = Math.min(total, start + maxVis - 1);
    if (end - start < maxVis - 1) start = Math.max(1, end - maxVis + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  getEndIndex(): number {
    return Math.min((this.pageIndex + 1) * this.pageSize, this.dataSource.length);
  }

  getPaginationRange(): string {
    if (this.dataSource.length === 0) return '0-0 of 0 items';
    return `${this.pageIndex * this.pageSize + 1}-${this.getEndIndex()} of ${this.dataSource.length} items`;
  }

  goToFirstPage(): void { if (this.pageIndex !== 0) this.pageIndex = 0; }
  goToPrevPage():  void { if (this.pageIndex > 0) this.pageIndex--; }
  goToNextPage():  void { if (this.pageIndex < this.getTotalPages() - 1) this.pageIndex++; }
  goToLastPage():  void { this.pageIndex = this.getTotalPages() - 1; }
  goToPage(index: number): void {
    if (index >= 0 && index < this.getTotalPages()) this.pageIndex = index;
  }

  // ═══════════════════════════════════════════════════════════
  //  HELPERS
  // ═══════════════════════════════════════════════════════════

  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  getTotalLeadsLabel(): string {
    const total = this.dataSource.length;
    if (total === 0) return 'No results';
    return `Search found ${total} row${total !== 1 ? 's' : ''}`;
  }
}